/**
 * 배포 직후 라우트 청크 로딩 실패 복구.
 *
 * 새 버전이 배포되면 이미 열려 있던 화면(옛 index.html)이 가리키는 hashed chunk 가 사라져
 * 메뉴 이동 시 dynamic import 가 실패한다. 현재 화면을 새로고침하면 사용자가 누른 메뉴로
 * 가지 못하므로, **이동하려던 주소**를 전체 로드해 최신 index.html 과 함께 그 화면을 연다.
 */

/** Chrome·Edge / Firefox / Safari / Vite CSS preload / webpack 식 메시지 */
const CHUNK_LOAD_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|Loading (CSS )?chunk \S+ failed/i;

/** sessionStorage — 같은 주소로 연달아 재시도하는 무한 루프 방지 */
export const CHUNK_RELOAD_STORAGE_KEY = 'ax:chunk-reload' as const;

/** 같은 주소 재시도를 막는 시간 — 전체 로드 후에도 실패하면 청크가 진짜 없는 것 */
export const CHUNK_RELOAD_RETRY_WINDOW_MS = 10_000;

export function isChunkLoadError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err);
  return CHUNK_LOAD_ERROR_PATTERN.test(message);
}

/**
 * `targetHref` 로 전체 로드해도 되는지. 직전에 같은 주소로 시도했으면 false.
 * storage 를 쓸 수 없으면(사생활 보호 모드 등) 루프 판정 없이 true.
 */
export function shouldReloadForChunkError(
  targetHref: string,
  storage: Storage | null,
  now: number = Date.now(),
): boolean {
  if (!storage) return true;
  try {
    const raw = storage.getItem(CHUNK_RELOAD_STORAGE_KEY);
    if (raw) {
      const last = JSON.parse(raw) as { href?: unknown; at?: unknown };
      if (
        last.href === targetHref &&
        typeof last.at === 'number' &&
        now - last.at < CHUNK_RELOAD_RETRY_WINDOW_MS
      ) {
        return false;
      }
    }
    storage.setItem(CHUNK_RELOAD_STORAGE_KEY, JSON.stringify({ href: targetHref, at: now }));
  } catch {
    // 손상된 값·쓰기 거부 — 루프 판정만 포기하고 이동은 진행
  }
  return true;
}

export function safeSessionStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}
